/* eslint-disable array-bracket-newline */
/**
 * Row with wallet
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Network, enums } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import ColorPicker from '../../../components/Watchlist/ColorPicker';
import UnfavouriteStar from '../../../components/Watchlist/UnfavouriteStar';
import { FavStep } from '../../../components/Watchlist/WatchlistRow';
import { WatchlistWallet } from '../../../reducers/wallet_watchlist';
import NumberDisplay from '../../../molecules/NumberDisplay';
import { GetWalletFromWatchlistData } from '../../../selectors/watchlist';
import { capitalize } from '../../../components/Modal/TickersModal/helpers';
import { UpdateWatchlistItemPayload } from '../../../reducers/watchlist';
import WalletField from '../../../molecules/WalletField';
import { createGetFromWalletTags } from '../../../selectors/walletTags';

interface IWatchlistRowProps {
  item: WatchlistWallet;
  actionSwitchCurrent?: (network: string, address: string) => void;
  actionRemoveWatchlist?: (id: number, callback: () => void) => void;
  actionAddWatchlist?: (
    data: GetWalletFromWatchlistData,
    tag?: enums.Tag,
    callback?: () => void
  ) => void;
  actionUpdateWatchlist?: (
    id: number,
    payload: UpdateWatchlistItemPayload
  ) => void;
  active: boolean;
  id?: number;
}

const WatchlistRow = ({
  item,
  actionSwitchCurrent,
  actionRemoveWatchlist,
  actionAddWatchlist,
  actionUpdateWatchlist,
  active,
  id,
}: IWatchlistRowProps) => {
  const selectFromWalletTags = useMemo(
    () => createGetFromWalletTags(item.address, item.chain),
    [item.address, item.chain]
  );

  const currentTag = useSelector(selectFromWalletTags);

  const [isColorPicker, setColorPicker] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [favStep, setFavStep] = useState<FavStep>(FavStep.default);
  const [tag, setTag] = useState<enums.Tag>();

  const ref = useRef<HTMLDivElement>(null);

  const isBigTradeWallet = 'value_usd' in item;
  const isActiveWallet = 'volume_24' in item;

  useEffect(() => {
    if (favStep === FavStep.color) {
      setColorPicker(true);
    }
    if (favStep === FavStep.favourite && ref.current) {
      setLoading(true);
      ref.current.classList.add('watchlist__row--remove');
      setTimeout(() => {
        if (actionAddWatchlist && tag) {
          const data: GetWalletFromWatchlistData =
            isBigTradeWallet || isActiveWallet
              ? { type: 'traders', chain: item.chain, address: item.address }
              : { type: 'overview', walletId: item.wallet_id };
          actionAddWatchlist(data, tag, actionCallback);
          setTag(undefined);
          setFavStep(FavStep.default);
          setColorPicker(false);
        }
      }, 150);
    }
  }, [actionAddWatchlist, favStep, item, tag]);

  const onSelect = useCallback(
    (e: any) => {
      if (actionSwitchCurrent) {
        e.stopPropagation();
        actionSwitchCurrent(item.chain, item.address);
      }
    },
    [actionSwitchCurrent, item]
  );

  const actionCallback = () => {
    if (ref.current) {
      ref.current.classList.remove('watchlist__row--remove');
      setLoading(false);
    }
  };

  const onUnfav = useCallback(() => {
    if (ref.current && !loading && id) {
      setLoading(true);
      ref.current.classList.add('watchlist__row--remove');
      setTimeout(() => {
        if (actionRemoveWatchlist) {
          actionRemoveWatchlist(id, actionCallback);
        }
      }, 150);
    }
  }, [actionRemoveWatchlist, id, loading]);

  const onFav = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (!loading) {
        setFavStep(FavStep.color);
      }
    },
    [loading]
  );

  const onUpdateTag = useCallback(
    (tag: enums.Tag) => {
      if (favStep === FavStep.color) {
        setTag(tag);
        setFavStep(FavStep.favourite);
      } else if (actionUpdateWatchlist && id) {
        actionUpdateWatchlist(id, { tag });
      }
    },
    [actionUpdateWatchlist, favStep, id]
  );

  const rect = ref.current?.getBoundingClientRect();

  const nickname = capitalize(item.nickname);

  let itemValue: number;
  if (isActiveWallet) {
    itemValue = item.volume_24;
  } else if (isBigTradeWallet) {
    itemValue = item.value_usd;
  } else {
    itemValue = item.balance_usd;
  }

  return (
    <div
      ref={ref}
      className={`watchlist__row ${active ? 'watchlist__row--active' : ''}`}
      onClick={onSelect}
      onContextMenu={(e) => {
        if (actionUpdateWatchlist) {
          e.preventDefault();
          setColorPicker(!isColorPicker);
        }
      }}
    >
      <div className='watchlist__row__icon_column watchlist__row__icon_column--left'>
        {actionRemoveWatchlist && (
          <UnfavouriteStar
            tag={'tag' in item && item.tag ? item.tag : enums.Tag.Blue}
            onUnfav={onUnfav}
          />
        )}
        {actionAddWatchlist && (
          <div
            className='favourite icon-star'
            data-qa='addFavourite'
            onClick={onFav}
          />
        )}
      </div>

      <div className='watchlist__row__content watchlist__row__content--no_alert'>
        <div className='watchlist__row__content__item'>
          <Text
            className='watchlist__row__exchange watchlist__row__exchange--text'
            style={{ title: nickname }}
            size={12}
          >
            {currentTag || nickname || 'Unnamed'}
          </Text>
          <div className='watchlist__row__text'>
            <WalletField
              wallet={item.address}
              network={item.chain}
              textProps={{
                size: 14,
                className: 'text--grey-200',
                style: { title: item.address },
              }}
              showTag={false}
            />
          </div>
        </div>
        <div className='watchlist__row__content__item'>
          <div className='watchlist__row__text'>
            {isBigTradeWallet && (
              <Text size={12} className='text--grey-400 text--weight-500'>
                Trade Value:{' '}
              </Text>
            )}
            {isActiveWallet && (
              <Text size={12} className='text--grey-400 text--weight-500'>
                Volume 24H:{' '}
              </Text>
            )}
            <NumberDisplay
              value={itemValue}
              round={isBigTradeWallet || isActiveWallet ? 1 : 2}
              startSymbol='$'
              textProps={{
                size: 14,
                className: 'text--grey-200',
                style: {
                  title: itemValue.toString(),
                },
              }}
            />
          </div>
          <Text className='watchlist__row--neutral' size={10}>
            {Network.isValidChain(item.chain)
              ? Network.fromChain(item.chain).getTitle()
              : item.chain}
          </Text>
          {isColorPicker && rect && (
            <ColorPicker
              top={rect.top - 80}
              left={rect.left - 5}
              onClose={() => {
                if (favStep === FavStep.color) {
                  onUpdateTag(enums.Tag.Blue);
                }
                setColorPicker(false);
              }}
              activeTag={'tag' in item ? item.tag : undefined}
              onSelect={onUpdateTag}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(WatchlistRow);
